import { FC } from "react";
import { Image } from "antd";
import { TServiceTicketSupplyItem } from "~/types";
import { TWaybillService } from "~/types/waybill-service";
import {
  capitalize,
  cn,
  formatDate,
  formatFullAddress,
  formatPackingType,
  formatToAMPM,
} from "~/utils";
import { ServiceType } from "~/utils/constants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const calculateTotalPackages = (ticket: TWaybillService) => {
  const standalonePkgs =
    ticket.inventories?.filter((item: any) => !item.isContainer).length || 0;
  const containers =
    ticket.inventories?.filter((item: any) => item.isContainer).length || 0;
  return standalonePkgs + containers;
};

export const WaybillContent: FC<{
  ticket: TWaybillService;
  inventories: any[];
  supplies: TServiceTicketSupplyItem[];
  isFirstPage: boolean;
  pageNumber: number;
  totalOddPages: number;
}> = ({
  ticket,
  inventories,
  supplies,
  isFirstPage,
  pageNumber,
  totalOddPages,
}) => {
  const formatDateTime = (date: Date | string) => {
    // The database stores times with 'Z' (UTC) but the time is actually local time
    // We need to strip the timezone and parse as local
    const dateStr = typeof date === 'string' ? date : date.toISOString();
    const localDateStr = dateStr.replace('Z', '').replace(/\.\d{3}/, ''); // Remove 'Z' and milliseconds
    const d = dayjs(localDateStr);

    return {
      date: d.format('MM/DD/YYYY'),
      time: d.format('h:mm A'),
    };
  };

  const timeIn = ticket.timeIn ? formatDateTime(ticket.timeIn) : null;
  const timeOut = ticket.timeOut ? formatDateTime(ticket.timeOut) : null;
  return (
    <div className="waybill-page content-page">
      <div className="waybill-grid">
        {/* Header Section - Always show */}
        <div className="header" style={{ backgroundColor: "#f0f0f0" }}>
          <div className="header-left">
            <h1>
              <strong>WAYBILL / WORK ORDER</strong>
            </h1>
            <p>
              ART HANDLER COLLECTIVE | 15 Cumberland St, Brooklyn NY, 11205 |
              646 741 4047 | www.arthandler.co
            </p>
          </div>
          <div className="header-right">
            <h1>
              <strong className="uppercase">
                {capitalize(ticket.serviceType)}
              </strong>
            </h1>
            <h1>
              <strong>{ticket?.booking?.referenceNumber}</strong>
            </h1>
            <div className="flex gap-2">
              <p>
                <strong>Date:</strong>{" "}
                {ticket.serviceDatetime
                  ? new Date(ticket.serviceDatetime).toLocaleDateString()
                  : new Date().toLocaleDateString()}
              </p>
              <p>
                <strong>Ref #:</strong> {ticket.serial}
              </p>
            </div>
          </div>
        </div>

        {/* Client Sections - Always show */}
        <div className="client-sections">
          {/* Booking Client (Left Side) */}
          <div className="booking-client-box">
            <h3>BOOKING CLIENT</h3>
            <p>
              <strong>Name:</strong>{" "}
              {ticket?.booking?.client?.companyName || "Unnamed Client"}
            </p>
            <p>
              <strong>Contact:</strong>{" "}
              {`${ticket?.booking?.client?.companyName || "Unnamed Client"} (${
                ticket?.booking?.client?.contactPhone || ""
              })`}
            </p>
          </div>

          {/* Service-Specific Client (Right Side) */}
          <div className="service-client-box">
            {ticket.serviceType === ServiceType.COLLECTION && (
              <div className="sender-box">
                <h3>SHIPPER</h3>
                <p>
                  <strong>Name:</strong>{" "}
                  {ticket?.clientService?.companyName || "Unnamed Client"}
                </p>
                <p>
                  <strong>Address:</strong>{" "}
                  {(ticket?.location && formatFullAddress(ticket?.location)) ||
                    "No address"}
                </p>
                <p>
                  <strong>Contact:</strong>{" "}
                  {`${
                    ticket?.clientService?.companyName || "Unnamed Client"
                  } (${ticket?.clientService?.contactPhone || ""})`}
                </p>
              </div>
            )}
            {ticket.serviceType === ServiceType.DELIVERY && (
              <div className="receiver-box">
                <h3>CONSIGNEE</h3>
                <p>
                  <strong>Name:</strong>{" "}
                  {ticket?.clientService?.companyName || "Unnamed Client"}
                </p>
                <p>
                  <strong>Address:</strong>{" "}
                  {formatFullAddress(ticket?.location) || "No address"}
                </p>
                <p>
                  <strong>Contact:</strong>{" "}
                  {`${
                    ticket?.clientService?.companyName || "Unnamed Client"
                  } (${ticket?.clientService?.contactPhone || ""})`}
                </p>
              </div>
            )}
            {ticket.serviceType === ServiceType.WAREHOUSE_SERVICE && (
              <div className="sender-box">
                <h3>CLIENT</h3>
                <p>
                  <strong>Name:</strong>{" "}
                  {ticket?.clientService?.companyName || "Unnamed Client"}
                </p>
                <p>
                  <strong>Address:</strong>{" "}
                  {formatFullAddress(ticket?.location) || "No address"}
                </p>
                <p>
                  <strong>Contact:</strong>{" "}
                  {`${
                    ticket?.clientService?.companyName || "Unnamed Client"
                  } (${ticket?.clientService?.contactPhone || ""})`}
                </p>
              </div>
            )}
            {ticket.serviceType === ServiceType.FABRICATION_SERVICE && (
              <div className="sender-box">
                <h3>MAIN CLIENT</h3>
                <p>
                  <strong>Name:</strong>{" "}
                  {ticket?.booking?.client?.companyName || "Unnamed Client"}
                </p>
                <p>
                  <strong>Service Client:</strong>{" "}
                  {ticket?.clientService?.companyName || "Unnamed Client"}
                </p>
              </div>
            )}
            {ticket.serviceType === ServiceType.ON_SITE_SERVICE && (
              <div className="sender-box">
                <h3>SERVICE CLIENT</h3>
                <p>
                  <strong>Name:</strong>{" "}
                  {ticket?.clientService?.companyName || "Unnamed Client"}
                </p>
                <p>
                  <strong>Address:</strong>{" "}
                  {formatFullAddress(ticket?.location) || "No address"}
                </p>
                <p>
                  <strong>Contact:</strong>{" "}
                  {`${
                    ticket?.clientService?.companyName || "Unnamed Client"
                  } (${ticket?.clientService?.contactPhone || ""})`}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Service Details Section - Always show */}
        <div className="service-details">
          <h3>Service Details</h3>
          <div
            className={cn(
              "service-instructions-box grid grid-cols-1 gap-4",
              ticket?.booking?.project && "grid-cols-2"
            )}
          >
            <div className="">
              <p>
                <strong>Additional Services:</strong>{" "}
                {ticket.addOnServices?.map((service, index) => (
                  <span key={index}>{service.name}</span>
                ))}
              </p>
              <p>
                <strong>Scope of Work:</strong>{" "}
                {ticket?.scope || "Not specified"}
              </p>
            </div>
            <div>
              {ticket?.booking?.project && (
                <p>
                  <strong>Project:</strong> {ticket?.booking?.project.name}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Inventory Items Section */}
        {inventories.length > 0 && (
          <div className="inventory-items">
            <div className="flex justify-between">
              <h3>Inventory Items {!isFirstPage && "(Continued)"}</h3>
              {isFirstPage && (
                <p>
                  <strong>Total Packages:</strong>{" "}
                  {calculateTotalPackages(ticket)}
                </p>
              )}
            </div>
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Inventory Ref</th>
                  <th>Client Ref</th>
                  <th>Artist Name</th>
                  <th>Type</th>
                  <th>Packing</th>
                  <th>Dimensions LWH (in)</th>
                  <th>Weight</th>
                </tr>
              </thead>
              <tbody>
                {inventories.map((item) => (
                  <tr key={item.id}>
                    <td>{item.title || ""}</td>
                    <td>{item.inventoryRef || ""}</td>
                    <td>{item.clientInternalRef || ""}</td>
                    <td>{item.artistName || "Unknown"}</td>
                    <td>{item.itemType || ""}</td>
                    <td>{formatPackingType(item)}</td>
                    <td>{`${item.length || 0} × ${item.width || 0} × ${
                      item.height || 2
                    } in`}</td>
                    <td>{item.weight || 13} lb</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Supplies Section */}
        {supplies.length > 0 && (
          <div className="supplies">
            <h3>Supplies {!isFirstPage && "(Continued)"}</h3>
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Quantity</th>
                  <th>Unit</th>
                  <th>Total Used</th>
                </tr>
              </thead>
              <tbody>
                {supplies.map((item) =>
                  item.supply ? (
                    <tr key={item.supply.id}>
                      <td>{item.supply.name || ""}</td>
                      <td>{item.quantity || ""}</td>
                      <td>{item.supply.unit || ""}</td>
                      <td></td>
                    </tr>
                  ) : null
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Notes Section - Always show but smaller on continuation pages */}
        <div className="confirmation">
          <h3>Completion Notes</h3>
          <div
            className={`confirmation-box ${
              !isFirstPage ? "min-h-10" : "min-h-10"
            }`}
            style={{ whiteSpace: 'pre-wrap', padding: '8px' }}
          >
            {ticket.completionNotes || ''}
          </div>
        </div>

        {/* Confirmation Section - Always show */}
        <div className="confirmation">
          <h3>Confirmation</h3>
          <div className="confirmation-box">
            <div className="grid grid-cols-2">
              <p>
                <strong>Time In:</strong>
                {"  "}
                {timeIn
                  ? `${timeIn.date} at ${timeIn.time}`
                  : "______________________________________________________________"}
              </p>
              <p>
                <strong>Time Out:</strong>
                {"  "}
                {timeOut
                  ? `${timeOut.date} at ${timeOut.time}`
                  : "______________________________________________________________"}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex w-full flex-col">
                <p>
                  <strong>Shipper/Receiver:</strong>{" "}
                  {!(ticket.completedAt || ticket.completed_at) &&
                    !ticket.jobLeadSignature &&
                    "_______________________"}
                </p>
                <div className="flex w-full flex-col align-item justify-center">
                  {(ticket.completedAt || ticket.completed_at) && ticket.clientSignature ? (
                    <>
                      <Image
                        src={ticket.clientSignature}
                        alt="Client Signature"
                        width="100%"
                        height={50}
                        style={{ objectFit: "contain" }}
                        preview={{
                          mask: "View Signature",
                        }}
                      />
                      <p className="text-center">{ticket.clientFirstName}</p>
                    </>
                  ) : (
                    <>
                      <p>
                        <strong>Signature:</strong> _____________________
                      </p>
                      <p>
                        <strong>Print Name:</strong> _____________________
                      </p>
                    </>
                  )}
                </div>
              </div>
              <div className="flex w-full flex-col">
                <p>
                  <strong>Carrier:</strong>{" "}
                  {!(ticket.completedAt || ticket.completed_at) &&
                    !ticket.jobLeadSignature &&
                    "_______________________"}
                </p>
                <div className="flex w-full flex-col">
                  {(ticket.completedAt || ticket.completed_at) && ticket.jobLeadSignature ? (
                    <>
                      <Image
                        src={ticket.jobLeadSignature}
                        alt="Client Signature"
                        width="100%"
                        height={50}
                        style={{ objectFit: "contain" }}
                        preview={{
                          mask: "View Signature",
                        }}
                        className="mx-auto"
                      />
                      <p className="text-center">{ticket.jobLeadFirstName}</p>
                    </>
                  ) : (
                    <>
                      <p>
                        <strong>Signature:</strong> _____________________
                      </p>
                      <p>
                        <strong>Print Name:</strong> _____________________
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="page-footer">
        <p>
          Page {pageNumber} of {totalOddPages} | Waybill Information
        </p>
      </div>
    </div>
  );
};
